import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import TermsSection from './components/TermsSection';
import SEO from '../../components/SEO';
import TermsTableOfContents from './components/TermsTableOfContents';

const sections = [
  { id: 'acceptance', title: 'Acceptance of Terms' },
  { id: 'service-description', title: 'Service Description' },
  { id: 'account-responsibilities', title: 'Account Responsibilities' },
  { id: 'subscription-billing', title: 'Subscription & Billing' },
  { id: 'acceptable-use', title: 'Acceptable Use Policy' },
  { id: 'intellectual-property', title: 'Intellectual Property' },
  { id: 'data-ownership', title: 'Data Ownership' },
  { id: 'service-availability', title: 'Service Availability' },
  { id: 'termination', title: 'Termination' },
  { id: 'governing-law', title: 'Governing Law' },
  { id: 'contact', title: 'Contact Information' },
];

const TermsOfUse = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ fontFamily: "'DM Sans', sans-serif", backgroundColor: '#F8F9FC' }}>
      <SEO
        title="Terms of Use — ADU Flow"
        description="Review ADU Flow's terms of use and service agreement for our ADU construction CRM and project management software platform."
        canonical="/terms-of-use"
        keywords="ADU Flow terms of use, terms of service, ADU construction CRM terms, service agreement, legal terms"
      />
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800&display=swap');`}</style>

      {/* Header */}
      <header style={{ background: '#0D2137', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-sm font-medium transition-colors hover:text-orange-400"
              style={{ color: 'rgba(255,255,255,0.65)' }}
            >
              <ArrowLeft size={16} />
              Back to Home
            </button>
            <img
              src="/assets/images/adu_flow_logo.png"
              alt="ADU Flow"
              className="h-8 w-auto object-contain cursor-pointer"
              style={{ filter: 'brightness(0) invert(1)' }}
              onClick={() => navigate('/')}
            />
          </div>
        </div>
      </header>

      {/* Hero */}
      <div style={{ background: 'linear-gradient(135deg, #0D2137 0%, #1B3A5C 100%)' }} className="py-14 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold mb-5"
            style={{ background: 'rgba(232,115,42,0.15)', color: '#f08040', border: '1px solid rgba(232,115,42,0.3)' }}
          >
            <FileText size={14} />
            Legal Document
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3" style={{ letterSpacing: '-0.02em' }}>
            Terms of Use
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.55)' }} className="text-sm">
            Last updated: March 4, 2026 &nbsp;·&nbsp; Effective: March 4, 2026
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-4 gap-10">
          {/* Sidebar TOC */}
          <aside className="hidden lg:block lg:col-span-1">
            <TermsTableOfContents sections={sections} />
          </aside>

          {/* Main Content */}
          <main className="lg:col-span-3">
            <div className="rounded-2xl bg-white p-8 sm:p-10" style={{ border: '1px solid rgba(13,33,55,0.08)', boxShadow: '0 4px 24px rgba(13,33,55,0.06)' }}>
              <p className="text-sm mb-8 leading-relaxed" style={{ color: '#6B7280' }}>
                These Terms of Use ("Terms") govern your access to and use of ADU Flow, a CRM platform operated by ADU Flow ("Company", "we", "us", or "our"). Please read these Terms carefully before using our services. By accessing or using ADU Flow, you agree to be bound by these Terms.
              </p>

              <TermsSection id="acceptance" title="1. Acceptance of Terms" defaultOpen={true}>
                <p className="text-sm">By creating an account, accessing, or using ADU Flow, you confirm that:</p>
                <ul className="text-sm space-y-2 mt-2 ml-4 list-disc">
                  <li>You are at least 18 years of age and have the legal capacity to enter into binding agreements.</li>
                  <li>You are authorized to act on behalf of your organization if registering for a business account.</li>
                  <li>You have read, understood, and agree to these Terms and our Privacy Policy.</li>
                  <li>You will comply with all applicable laws and regulations in your use of the platform.</li>
                </ul>
                <p className="text-sm mt-3">If you do not agree to these Terms, you must not use ADU Flow. We reserve the right to update these Terms at any time. Continued use after changes constitutes acceptance.</p>
              </TermsSection>

              <TermsSection id="service-description" title="2. Service Description">
                <p className="text-sm">ADU Flow provides a cloud-based CRM platform designed for ADU (Accessory Dwelling Unit) construction companies, including:</p>
                <ul className="text-sm space-y-2 mt-2 ml-4 list-disc">
                  <li>Project management and pipeline tracking for ADU construction projects.</li>
                  <li>Customer relationship management and contact database tools.</li>
                  <li>Permit tracking and deadline management for construction permits.</li>
                  <li>Contractor and team member management with role-based access controls.</li>
                  <li>Reporting, analytics, and calendar scheduling features.</li>
                </ul>
                <p className="text-sm mt-3">We reserve the right to modify, suspend, or discontinue any feature of the service at any time with reasonable notice. We will not be liable for any modification, suspension, or discontinuation of services.</p>
              </TermsSection>

              <TermsSection id="account-responsibilities" title="3. Account Responsibilities">
                <p className="text-sm">As an account holder, you are responsible for:</p>
                <ul className="text-sm space-y-2 mt-2 ml-4 list-disc">
                  <li><strong>Account Security:</strong> Maintaining the confidentiality of your login credentials and immediately notifying us of any unauthorized access.</li>
                  <li><strong>Accurate Information:</strong> Providing accurate, current, and complete information during registration and keeping it updated.</li>
                  <li><strong>Team Members:</strong> All actions taken by users you invite to your organization account. You are responsible for ensuring your team members comply with these Terms.</li>
                  <li><strong>Data Accuracy:</strong> The accuracy and legality of all data you enter into the platform, including customer information and project records.</li>
                </ul>
                <p className="text-sm mt-3">One account per organization is permitted unless otherwise agreed in writing. Sharing account credentials with unauthorized parties is prohibited.</p>
              </TermsSection>

              <TermsSection id="subscription-billing" title="4. Subscription & Billing">
                <p className="text-sm">ADU Flow operates on a subscription basis with the following terms:</p>
                <ul className="text-sm space-y-2 mt-2 ml-4 list-disc">
                  <li><strong>Free Trial:</strong> New accounts receive one free month. No credit card is required to start the trial.</li>
                  <li><strong>Subscription Fee:</strong> After the trial period, the standard rate is $39/month per organization, billed monthly.</li>
                  <li><strong>Billing Cycle:</strong> Subscriptions renew automatically on a monthly basis unless cancelled.</li>
                  <li><strong>Payment Processing:</strong> Payments are processed securely through Stripe. By providing payment information, you authorize recurring charges.</li>
                  <li><strong>Cancellation:</strong> You may cancel your subscription at any time. Access continues until the end of the current billing period. No partial refunds are issued for unused time.</li>
                  <li><strong>Price Changes:</strong> We will provide at least 30 days notice before any price changes take effect.</li>
                  <li><strong>Failed Payments:</strong> If payment fails, we will attempt to retry. Accounts with persistent failed payments may be suspended after reasonable notice.</li>
                </ul>
              </TermsSection>

              <TermsSection id="acceptable-use" title="5. Acceptable Use Policy">
                <p className="text-sm">You agree to use ADU Flow only for lawful purposes. The following activities are strictly prohibited:</p>
                <ul className="text-sm space-y-2 mt-2 ml-4 list-disc">
                  <li>Uploading or transmitting malicious code, viruses, or any software intended to damage or interfere with the platform.</li>
                  <li>Attempting to gain unauthorized access to other accounts, systems, or networks connected to ADU Flow.</li>
                  <li>Using the platform to store or transmit content that is illegal, defamatory, or infringes on third-party rights.</li>
                  <li>Reverse engineering, decompiling, or attempting to extract the source code of the platform.</li>
                  <li>Using automated tools to scrape, crawl, or extract data from the platform without written permission.</li>
                  <li>Reselling, sublicensing, or providing access to the platform to third parties without authorization.</li>
                  <li>Violating any applicable local, state, national, or international laws or regulations.</li>
                </ul>
                <p className="text-sm mt-3">Violation of this policy may result in immediate account suspension or termination without refund.</p>
              </TermsSection>

              <TermsSection id="intellectual-property" title="6. Intellectual Property">
                <p className="text-sm">All intellectual property rights in ADU Flow belong to the Company:</p>
                <ul className="text-sm space-y-2 mt-2 ml-4 list-disc">
                  <li><strong>Platform Ownership:</strong> The ADU Flow software, design, code, trademarks, and brand assets are owned exclusively by the Company.</li>
                  <li><strong>License Grant:</strong> We grant you a limited, non-exclusive, non-transferable license to use the platform solely for your internal business purposes during your subscription.</li>
                  <li><strong>Restrictions:</strong> You may not copy, modify, distribute, sell, or create derivative works based on the platform without express written consent.</li>
                  <li><strong>Feedback:</strong> Any suggestions or feedback you provide may be used by us to improve the platform without compensation or attribution.</li>
                </ul>
              </TermsSection>

              <TermsSection id="data-ownership" title="7. Data Ownership">
                <p className="text-sm">Your business data remains yours:</p>
                <ul className="text-sm space-y-2 mt-2 ml-4 list-disc">
                  <li><strong>Your Data:</strong> All customer records, project data, and business information you enter into ADU Flow remains your property.</li>
                  <li><strong>Data Export:</strong> You may request an export of your data at any time by contacting support.</li>
                  <li><strong>License to Process:</strong> By using ADU Flow, you grant us a limited license to store, process, and display your data solely to provide the service to you.</li>
                  <li><strong>No Data Mining:</strong> We do not sell or use your business data for advertising or data brokerage purposes.</li>
                  <li><strong>Post-Cancellation:</strong> Your data is retained for 90 days after account cancellation, after which it is permanently deleted.</li>
                </ul>
              </TermsSection>

              <TermsSection id="service-availability" title="8. Service Availability">
                <p className="text-sm">We strive to maintain high availability but cannot guarantee uninterrupted service:</p>
                <ul className="text-sm space-y-2 mt-2 ml-4 list-disc">
                  <li><strong>Uptime Target:</strong> We target 99.5% monthly uptime, excluding scheduled maintenance windows.</li>
                  <li><strong>Maintenance:</strong> Scheduled maintenance will be communicated in advance when possible.</li>
                  <li><strong>Force Majeure:</strong> We are not liable for outages caused by events beyond our reasonable control, including internet infrastructure failures, natural disasters, or third-party service outages.</li>
                  <li><strong>No Warranty:</strong> The service is provided "as is" and "as available" without warranties of any kind, express or implied, including warranties of merchantability or fitness for a particular purpose.</li>
                </ul>
              </TermsSection>

              <TermsSection id="termination" title="9. Termination">
                <p className="text-sm">Either party may terminate the service relationship:</p>
                <ul className="text-sm space-y-2 mt-2 ml-4 list-disc">
                  <li><strong>By You:</strong> Cancel your subscription at any time through account settings or by contacting support. Access continues until the billing period ends.</li>
                  <li><strong>By Us:</strong> We may suspend or terminate your account immediately if you violate these Terms, engage in fraudulent activity, or fail to pay subscription fees after notice.</li>
                  <li><strong>Effect of Termination:</strong> Upon termination, your right to access the platform ceases. Your data will be retained for 90 days before permanent deletion.</li>
                  <li><strong>Survival:</strong> Provisions regarding intellectual property, data ownership, and governing law survive termination.</li>
                </ul>
              </TermsSection>

              <TermsSection id="governing-law" title="10. Governing Law">
                <p className="text-sm">These Terms are governed by the laws of the State of California, United States, without regard to conflict of law principles.</p>
                <ul className="text-sm space-y-2 mt-3 ml-4 list-disc">
                  <li><strong>Dispute Resolution:</strong> Any disputes arising from these Terms or your use of ADU Flow shall first be attempted to be resolved through good-faith negotiation.</li>
                  <li><strong>Arbitration:</strong> If negotiation fails, disputes shall be resolved through binding arbitration in accordance with the American Arbitration Association rules.</li>
                  <li><strong>Jurisdiction:</strong> You consent to the exclusive jurisdiction of courts located in California for any matters not subject to arbitration.</li>
                  <li><strong>Class Action Waiver:</strong> You waive any right to participate in class action lawsuits or class-wide arbitration against ADU Flow.</li>
                </ul>
              </TermsSection>

              <TermsSection id="contact" title="11. Contact Information">
                <p className="text-sm">For questions about these Terms of Use, contact us:</p>
                <ul className="text-sm space-y-2 mt-3 ml-4 list-disc">
                  <li><strong>Email:</strong> <a href="mailto:legal@aduflow.net" style={{ color: '#E8732A' }}>legal@aduflow.net</a></li>
                  <li><strong>Website:</strong> <a href="https://aduflow.net" style={{ color: '#E8732A' }}>aduflow.net</a></li>
                </ul>
                <p className="text-sm mt-3">These Terms constitute the entire agreement between you and ADU Flow regarding your use of the service and supersede all prior agreements.</p>
              </TermsSection>
            </div>

            {/* Footer nav */}
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-2 text-sm font-medium transition-colors hover:text-orange-500"
                style={{ color: '#6B7280' }}
              >
                <ArrowLeft size={16} />
                Back to Home
              </button>
              <button
                onClick={() => navigate('/privacy-policy')}
                className="text-sm font-medium transition-colors hover:text-orange-500"
                style={{ color: '#E8732A' }}
              >
                View Privacy Policy →
              </button>
            </div>
          </main>
        </div>
      </div>

      {/* Footer */}
      <footer className="mt-12 py-8 px-4 text-center" style={{ background: '#0D2137', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>&copy; 2026 ADU Flow. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default TermsOfUse;
